import * as React from "react";
import { cn } from "@/lib/cn";
import { ChartLegend, type LegendItem } from "./ChartLegend";
import { ChartTooltip } from "./ChartTooltip";
import {
  areaPath,
  labelIndices,
  linePath,
  niceScale,
  useChartWidth,
  useHoverIndex,
  type Pt,
} from "./chart-utils";

/**
 * How a series is drawn. The kind is part of the *reading*, not decoration:
 *
 * `area` is a volume you compare others against, `line` is a quantity tracked
 * over time, and `bar` is a discrete per-period count that never gets
 * interpolated between two days.
 *
 * There was a `dashed` kind, for a second quantity meant to be read as a
 * shortfall against the first. Nothing used it in the end: what tells two
 * series apart is hue in the legend against hue on the figure, which works at
 * any size, where a 2px dash pattern at chart scale is a smudge.
 */
export interface TrendSeries {
  key: string;
  label: string;
  /** A `chart-tone--*` suffix: `accent`, `ok`, `advisory`, `stop`, … */
  tone: string;
  kind?: "area" | "line" | "bar";
}

export interface TrendPoint {
  key: string;
  /** The axis label. Short - it has to survive a half-width card. */
  label: string;
  /** The tooltip's heading, when the axis label is too terse to stand alone. */
  title?: string;
  /**
   * A null is "nothing to measure in this period", which is NOT zero: a
   * compliance rate over a week with no visits does not exist, and plotting it
   * on the floor draws a crisis out of a quiet week. An absent period gets no
   * vertex, no mark and nothing in the crosshair, and the line runs from the
   * reading before it to the reading after - see `linePath` in `chart-utils`,
   * which carries the four other things this has been.
   */
  values: Record<string, number | null>;
}

/**
 * A multi-series time chart with a real Y axis, a hover crosshair and a legend.
 *
 * ── Why it measures itself ─────────────────────────────────────────────────
 *
 * The chart it replaces used a fixed 640×200 viewBox at `width: 100%`, which
 * scales the *text* with the box: the same 9px tick renders at 7px in a
 * third-width card and 13px in a full-width one, on the same page. Measuring
 * with `useChartWidth` gives one CSS pixel per user unit, so a label is the size
 * the stylesheet asked for wherever the chart lands.
 *
 * ── Why the axis is worth its 34 pixels ────────────────────────────────────
 *
 * Without one, the reader can see that Tuesday is taller than Monday and cannot
 * see by how much - which is the difference between a sparkline and a chart. The
 * gridlines land on round numbers (`niceScale`), so the values are readable off
 * the axis rather than only out of the tooltip.
 *
 * ── `guide` ────────────────────────────────────────────────────────────────
 *
 * A horizontal reference line - a compliance target, the 90-second budget. A
 * percentage chart without one shows a number drifting; with one it shows a
 * number drifting *towards or away from something*, which is the only version an
 * EHS manager can act on.
 */
export function TrendChart({
  points,
  series,
  height = 236,
  unit = "",
  axisMax,
  guide,
  className,
}: {
  points: TrendPoint[];
  series: TrendSeries[];
  height?: number;
  /** Appended to every figure in the axis and the tooltip. `"%"`, `"s"`, `""`. */
  unit?: string;
  /** Fixes the axis top. Percentages want 100 so the ceiling means something. */
  axisMax?: number;
  guide?: { value: number; label: string };
  className?: string;
}) {
  const [ref, width] = useChartWidth();
  const hover = useHoverIndex(points.length);

  // `useId` emits colons, which are legal in an id attribute and not in the
  // `url(#…)` that references it. Stripped rather than hand-rolling a counter,
  // which would collide across two charts mounted from the same module.
  const gradientId = `trend-fill-${React.useId().replace(/[^a-zA-Z0-9]/g, "")}`;

  const PAD_L = 38;
  const PAD_R = 12;
  const PAD_T = 14;
  const PAD_B = 26;

  const plotW = Math.max(1, width - PAD_L - PAD_R);
  const plotH = Math.max(1, height - PAD_T - PAD_B);
  const baseline = PAD_T + plotH;

  const rawMax = Math.max(
    guide?.value ?? 0,
    // Nulls are absences, not zeroes, so they neither raise nor pin the axis.
    ...points.flatMap((point) =>
      series.map((s) => point.values[s.key]).filter((v): v is number => v != null)
    )
  );
  const scale =
    axisMax != null
      ? { max: axisMax, ticks: [0, axisMax / 4, axisMax / 2, (axisMax * 3) / 4, axisMax] }
      : niceScale(rawMax);

  // Bars need a slot each and lines sit at the slot's centre, so the two agree
  // about where "Tuesday" is. A line drawn edge to edge instead would put its
  // first point half a bar to the left of the first bar.
  const slot = plotW / Math.max(1, points.length);
  const x = (index: number) => PAD_L + slot * (index + 0.5);
  const y = (value: number) => baseline - (Math.max(0, value) / scale.max) * plotH;

  const pointsFor = (key: string): (Pt | null)[] =>
    points.map((point, index) => {
      const value = point.values[key];
      return value == null ? null : { x: x(index), y: y(value) };
    });

  const barSeries = series.filter((s) => s.kind === "bar");
  const barWidth = Math.max(3, Math.min(26, (slot * 0.62) / Math.max(1, barSeries.length)));

  const labelled = labelIndices(points.length, plotW);
  const format = (value: number) => `${round(value)}${unit}`;

  const hoveredPoint = points[hover.index];
  const hoverX = x(hover.index);

  // Anchored to the day's highest mark rather than to the top of the card, and
  // flipped below it when that mark is too near the ceiling to leave room.
  const hoverTop = Math.min(
    ...series
      .map((s) => hoveredPoint?.values[s.key])
      .filter((v): v is number => v != null)
      .map(y),
    baseline
  );
  const TIP_H = 30 + series.length * 20;
  const below = hoverTop < TIP_H + 16;

  function onMove(event: React.MouseEvent<SVGSVGElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const local = ((event.clientX - box.left) / box.width) * width;
    const index = Math.floor((local - PAD_L) / slot);
    hover.set(Math.max(0, Math.min(points.length - 1, index)));
  }

  // No `shape`. The swatch is a square for every series on every chart in the
  // console - see `ChartLegend`.
  const legend: LegendItem[] = series.map((s) => ({
    key: s.key,
    label: s.label,
    tone: s.tone,
  }));

  return (
    // The ref stays on this element in every branch - it is what the width is
    // measured from, and a chart that only mounts it once it has a width would
    // never get one.
    <div className={cn("chart", className)} ref={ref}>
      {points.length === 0 ? (
        <p className="chart-empty">Nothing recorded in this window yet.</p>
      ) : width === 0 ? (
        // Replaced on the layout pass, before the browser paints. It holds the
        // height so the card does not jump as the figure arrives.
        <div className="chart__pending" style={{ height }} />
      ) : (
        <>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            className="chart__figure"
            role="img"
            aria-label={`${series.map((s) => s.label).join(" and ")} over ${points.length} periods, ${points
              .map(
                (p) =>
                  `${p.label}: ${series
                    .map((s) => {
                      const v = p.values[s.key];
                      return `${s.label} ${v == null ? "not recorded" : `${v}${unit}`}`;
                    })
                    .join(", ")}`
              )
              .join("; ")}`}
            onMouseMove={onMove}
            onMouseLeave={() => hover.set(null)}
          >
            <defs>
              {series
                .filter((s) => s.kind === "area")
                .map((s) => (
                  <linearGradient
                    key={s.key}
                    id={`${gradientId}-${s.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                    className={cn("chart-tone--" + s.tone)}
                  >
                    <stop offset="0" className="chart__grad-top" />
                    <stop offset="1" className="chart__grad-bottom" />
                  </linearGradient>
                ))}
            </defs>

            {scale.ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD_L}
                  y1={y(tick)}
                  x2={width - PAD_R}
                  y2={y(tick)}
                  className={cn("chart__grid", tick === 0 && "chart__grid--base")}
                />
                <text x={PAD_L - 8} y={y(tick) + 3.5} className="chart__y-tick tnum">
                  {format(tick)}
                </text>
              </g>
            ))}

            {guide && (
              <g>
                <line
                  x1={PAD_L}
                  y1={y(guide.value)}
                  x2={width - PAD_R}
                  y2={y(guide.value)}
                  className="chart__guide"
                />
                <text x={width - PAD_R} y={y(guide.value) - 5} className="chart__guide-label">
                  {guide.label}
                </text>
              </g>
            )}

            {/* Bars first, so a line drawn over them stays readable. */}
            {barSeries.map((s, seriesIndex) =>
              points.map((point, index) => {
                const value = point.values[s.key];
                if (value == null || value <= 0) return null;
                const offset = (seriesIndex - (barSeries.length - 1) / 2) * (barWidth + 2);
                return (
                  <rect
                    key={`${s.key}-${point.key}`}
                    x={x(index) + offset - barWidth / 2}
                    y={y(value)}
                    width={barWidth}
                    height={Math.max(2, baseline - y(value))}
                    rx={Math.min(3, barWidth / 2)}
                    className={cn(
                      "chart__bar",
                      `chart-tone--${s.tone}`,
                      hover.hovering && hover.index !== index && "chart__bar--dim"
                    )}
                  />
                );
              })
            )}

            {series
              .filter((s) => s.kind !== "bar")
              .map((s) => {
                const pts = pointsFor(s.key);
                const line = linePath(pts);
                return (
                  <g key={s.key} className={cn("chart-tone--" + s.tone)}>
                    {s.kind === "area" && (
                      <path
                        d={areaPath(line, pts, baseline)}
                        fill={`url(#${gradientId}-${s.key})`}
                        stroke="none"
                      />
                    )}
                    <path d={line} className="chart__line" />
                  </g>
                );
              })}

            <line
              x1={PAD_L}
              y1={baseline}
              x2={width - PAD_R}
              y2={baseline}
              className="chart__axis"
            />

            {points.map((point, index) =>
              labelled.includes(index) ? (
                <text key={point.key} x={x(index)} y={height - 8} className="chart__x-tick">
                  {point.label}
                </text>
              ) : null
            )}

            {/* Always mounted and only faded - see `ChartTooltip`. */}
            <g className={cn("chart__cursor", hover.hovering && "chart__cursor--on")}>
              <line x1={hoverX} y1={PAD_T} x2={hoverX} y2={baseline} className="chart__crosshair" />
              {series
                .filter((s) => s.kind !== "bar")
                .map((s) => {
                  // No dot where there is no reading. A marker sitting on the
                  // axis is the same lie the interpolated line would have told.
                  const value = hoveredPoint?.values[s.key];
                  if (value == null) return null;
                  return (
                    <circle
                      key={s.key}
                      cx={hoverX}
                      cy={y(value)}
                      r={4}
                      className={cn("chart__dot", `chart-tone--${s.tone}`)}
                    />
                  );
                })}
            </g>
          </svg>

          <ChartTooltip
            open={hover.hovering}
            // Clamped so the tooltip cannot hang off either end and push a
            // horizontal scrollbar onto the whole dashboard.
            left={clamp((hoverX / Math.max(1, width)) * 100, 16, 84)}
            top={below ? hoverTop + 14 : hoverTop - TIP_H - 12}
            below={below}
            title={hoveredPoint?.title ?? hoveredPoint?.label ?? ""}
            rows={series.map((s) => {
              const value = hoveredPoint?.values[s.key];
              return {
                key: s.key,
                label: s.label,
                tone: s.tone,
                value: value == null ? "-" : format(value),
              };
            })}
          />

          <ChartLegend items={legend} />
        </>
      )}
    </div>
  );
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** One decimal at most, and never a trailing `.0` on a whole count. */
const round = (value: number) => Math.round(value * 10) / 10;
